import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, UpdateProfileData } from '../types';
import { authApi } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  login: (response: any) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      accessToken: null,
      isAuthenticated: false,
      login: (response: any) => {
        const token = response.tokens?.access_token || response.token;
        const idToken = response.tokens?.id_token || response.token;
        localStorage.setItem('token', token);
        localStorage.setItem('accessToken', token);
        localStorage.setItem('idToken', idToken);
        
        // デバッグ: 手動でadminロールを設定
        const userWithRole = { ...response.user, role: 'admin' as 'admin' | 'user' };
        
        set({ 
          token: idToken, 
          accessToken: token, 
          user: userWithRole, 
          isAuthenticated: true 
        });
        
        // ログイン後にユーザーのロール情報を取得
        const getUserRole = async () => {
          try {
            console.log('ロール取得開始...');
            const roleResponse = await authApi.getUserRole();
            console.log('ロール取得成功:', roleResponse);
            set((state) => ({ 
              ...state, 
              user: state.user ? { ...state.user, role: roleResponse.role as 'admin' | 'user' } : null
            }));
          } catch (error) {
            console.error('ロール取得に失敗:', error);
            // エラーの場合はデフォルトでadminロールを設定（デバッグ用）
            console.log('デバッグ: adminロールを強制設定');
            set((state) => ({ 
              ...state, 
              user: state.user ? { ...state.user, role: 'admin' as 'admin' | 'user' } : null
            }));
          }
        };
        
        // 少し遅延してロール情報を取得（トークンが設定されるまで待つ）
        setTimeout(getUserRole, 100);
      },
      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        set({ token: null, accessToken: null, user: null, isAuthenticated: false });
      },
      updateUser: (user: User) => {
        set((state) => ({ ...state, user }));
      },
      updateProfile: async (data: UpdateProfileData) => {
        const updatedUser = await authApi.updateProfile(data);
        set((state) => ({ ...state, user: updatedUser }));
      },
      clearAuth: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        set({ token: null, accessToken: null, user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
